import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styles from './AdminShared.module.scss';
import {
    ContentProcessorAnalyticsDto,
    ContentProcessorEventDto,
    ContentProcessorLogDto,
    PagedResult
} from '../../../types/admin.types';
import {
    fetchContentProcessorAnalytics,
    fetchContentProcessorEvents,
    fetchContentProcessorLogs
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { Routes } from '../../../types/constants';
import { isSuperAdmin } from '../../../utils/roles';
import AutoRefreshControl from './components/AutoRefreshControl';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh';

const pageSize = 25;
const emptyEvents: PagedResult<ContentProcessorEventDto> = {
    Results: [],
    PageIndex: 1,
    PageSize: pageSize,
    TotalPages: 0,
    TotalCount: 0
};
const emptyLogs: PagedResult<ContentProcessorLogDto> = {
    Results: [],
    PageIndex: 1,
    PageSize: pageSize,
    TotalPages: 0,
    TotalCount: 0
};

const toDateInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const normalizeGuidFilter = (value: string) => guidPattern.test(value.trim()) ? value.trim() : '';

const formatDateTime = (value?: string | null) => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

type ActivityTab = 'events' | 'logs';

const AdminContentProcessingActivity = () => {
    const { getUserInfo } = useUserState();
    const user = getUserInfo();
    const token = user?.token;
    const canView = isSuperAdmin(user?.roles);
    const [activeTab, setActiveTab] = useState<ActivityTab>('events');
    const [analytics, setAnalytics] = useState<ContentProcessorAnalyticsDto | null>(null);
    const [events, setEvents] = useState<PagedResult<ContentProcessorEventDto>>(emptyEvents);
    const [logs, setLogs] = useState<PagedResult<ContentProcessorLogDto>>(emptyLogs);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [eventPage, setEventPage] = useState(1);
    const [logPage, setLogPage] = useState(1);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [filters, setFilters] = useState(() => {
        const to = new Date();
        const from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return {
            from: toDateInput(from),
            to: toDateInput(to),
            severity: '',
            level: '',
            eventType: '',
            workerId: '',
            videoId: ''
        };
    });

    const apiRange = useMemo(() => {
        const fromUtc = filters.from ? new Date(`${filters.from}T00:00:00`).toISOString() : '';
        const toUtc = filters.to ? new Date(`${filters.to}T23:59:59`).toISOString() : '';
        return { fromUtc, toUtc };
    }, [filters.from, filters.to]);

    const loadActivity = useCallback(async () => {
        if (!canView) {
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);
        try {
            const videoId = normalizeGuidFilter(filters.videoId);
            const [analyticsResponse, eventsResponse, logsResponse] = await Promise.all([
                fetchContentProcessorAnalytics(apiRange.fromUtc, apiRange.toUtc, token),
                fetchContentProcessorEvents(eventPage, pageSize, {
                    fromUtc: apiRange.fromUtc,
                    toUtc: apiRange.toUtc,
                    severity: filters.severity,
                    eventType: filters.eventType,
                    workerId: filters.workerId,
                    videoId
                }, token),
                fetchContentProcessorLogs(logPage, pageSize, {
                    fromUtc: apiRange.fromUtc,
                    toUtc: apiRange.toUtc,
                    level: filters.level,
                    workerId: filters.workerId,
                    videoId
                }, token)
            ]);
            setAnalytics(analyticsResponse);
            setEvents(eventsResponse);
            setLogs(logsResponse);
        } catch {
            setErrorMessage('Unable to load content processor activity.');
        } finally {
            setIsLoading(false);
        }
    }, [
        apiRange.fromUtc,
        apiRange.toUtc,
        canView,
        eventPage,
        filters.eventType,
        filters.level,
        filters.severity,
        filters.videoId,
        filters.workerId,
        logPage,
        token
    ]);

    useEffect(() => {
        loadActivity();
    }, [loadActivity]);

    useAutoRefresh(autoRefresh, loadActivity);

    if (!canView) {
        return <Navigate to={Routes.ADMIN_ANALYTICS} replace />;
    }

    const resetFilters = () => {
        const to = new Date();
        const from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        setFilters({
            from: toDateInput(from),
            to: toDateInput(to),
            severity: '',
            level: '',
            eventType: '',
            workerId: '',
            videoId: ''
        });
        setEventPage(1);
        setLogPage(1);
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Content Processing Activity</h1>
                    <p>Review service analytics, worker events, and processing logs.</p>
                </div>
                <div className={styles.actions}>
                    <AutoRefreshControl checked={autoRefresh} onChange={setAutoRefresh} />
                    <button type="button" className={styles.secondary} onClick={loadActivity}>
                        Refresh
                    </button>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}

            <section className={styles.panel}>
                <h2>Filters</h2>
                <div className={styles.actions}>
                    <label className={styles.field}>
                        From
                        <input
                            type="date"
                            value={filters.from}
                            onChange={(event) => {
                                setEventPage(1);
                                setLogPage(1);
                                setFilters({ ...filters, from: event.target.value });
                            }}
                        />
                    </label>
                    <label className={styles.field}>
                        To
                        <input
                            type="date"
                            value={filters.to}
                            onChange={(event) => {
                                setEventPage(1);
                                setLogPage(1);
                                setFilters({ ...filters, to: event.target.value });
                            }}
                        />
                    </label>
                    <label className={styles.field}>
                        Worker ID
                        <input
                            value={filters.workerId}
                            onChange={(event) => {
                                setEventPage(1);
                                setLogPage(1);
                                setFilters({ ...filters, workerId: event.target.value });
                            }}
                            maxLength={200}
                        />
                    </label>
                    <label className={styles.field}>
                        Video ID
                        <input
                            value={filters.videoId}
                            onChange={(event) => {
                                setEventPage(1);
                                setLogPage(1);
                                setFilters({ ...filters, videoId: event.target.value });
                            }}
                            maxLength={200}
                        />
                    </label>
                    <button type="button" className={styles.secondary} onClick={resetFilters}>
                        Clear
                    </button>
                </div>
            </section>

            <section className={styles.panel}>
                <h2>Analytics</h2>
                {analytics ? (
                    <div className={styles.actions}>
                        <div className={styles.readOnlyBlock}>Events: {analytics.TotalEvents}</div>
                        <div className={styles.readOnlyBlock}>Logs: {analytics.TotalLogs}</div>
                        <div className={styles.readOnlyBlock}>Polls: {analytics.PollsStarted}</div>
                        <div className={styles.readOnlyBlock}>Skipped: {analytics.PollsSkipped}</div>
                        <div className={styles.readOnlyBlock}>Jobs completed: {analytics.JobsCompleted}</div>
                        <div className={styles.readOnlyBlock}>Jobs failed: {analytics.JobsFailed}</div>
                        <div className={styles.readOnlyBlock}>Errors: {analytics.Errors}</div>
                        <div className={styles.readOnlyBlock}>Last heartbeat: {formatDateTime(analytics.LastHeartbeatUtc)}</div>
                        {analytics.EventsByType.length > 0 ? (
                            <div className={styles.readOnlyBlock}>
                                Event types: {analytics.EventsByType.map((item) => `${item.Name} ${item.Count}`).join(', ')}
                            </div>
                        ) : null}
                        {analytics.LogsByLevel.length > 0 ? (
                            <div className={styles.readOnlyBlock}>
                                Log levels: {analytics.LogsByLevel.map((item) => `${item.Name} ${item.Count}`).join(', ')}
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <div className={styles.muted}>No activity analytics loaded.</div>
                )}
            </section>

            <section className={styles.panel}>
                <div className={styles.actions}>
                    <button
                        type="button"
                        className={activeTab === 'events' ? styles.primary : styles.secondary}
                        onClick={() => setActiveTab('events')}
                    >
                        Events
                    </button>
                    <button
                        type="button"
                        className={activeTab === 'logs' ? styles.primary : styles.secondary}
                        onClick={() => setActiveTab('logs')}
                    >
                        Logs
                    </button>
                    {activeTab === 'events' ? (
                        <>
                            <input
                                placeholder="Event type"
                                value={filters.eventType}
                                onChange={(event) => {
                                    setEventPage(1);
                                    setFilters({ ...filters, eventType: event.target.value });
                                }}
                            />
                            <select
                                value={filters.severity}
                                onChange={(event) => {
                                    setEventPage(1);
                                    setFilters({ ...filters, severity: event.target.value });
                                }}
                            >
                                <option value="">All severities</option>
                                <option value="info">Info</option>
                                <option value="warning">Warning</option>
                                <option value="error">Error</option>
                                <option value="fatal">Fatal</option>
                            </select>
                        </>
                    ) : (
                        <select
                            value={filters.level}
                            onChange={(event) => {
                                setLogPage(1);
                                setFilters({ ...filters, level: event.target.value });
                            }}
                        >
                            <option value="">All levels</option>
                            <option value="debug">Debug</option>
                            <option value="info">Info</option>
                            <option value="warning">Warning</option>
                            <option value="error">Error</option>
                            <option value="fatal">Fatal</option>
                        </select>
                    )}
                </div>

                {isLoading ? (
                    <div className={styles.muted}>Loading content processor activity...</div>
                ) : activeTab === 'events' ? (
                    <>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>Type</th>
                                    <th>Severity</th>
                                    <th>Worker</th>
                                    <th>Video</th>
                                    <th>Message</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.Results.map((item) => (
                                    <tr key={item.Id}>
                                        <td>{formatDateTime(item.OccurredAtUtc)}</td>
                                        <td>{item.EventType}</td>
                                        <td>{item.Severity}</td>
                                        <td>{item.WorkerId ?? '-'}</td>
                                        <td>{item.VideoId ?? '-'}</td>
                                        <td>{item.Message ?? '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {events.Results.length === 0 ? <div className={styles.empty}>No events found.</div> : null}
                        <div className={styles.actions}>
                            <button type="button" className={styles.secondary} disabled={eventPage <= 1} onClick={() => setEventPage((value) => Math.max(1, value - 1))}>
                                Prev
                            </button>
                            <span className={styles.muted}>Page {events.PageIndex} of {Math.max(1, events.TotalPages)}</span>
                            <button type="button" className={styles.secondary} disabled={eventPage >= events.TotalPages} onClick={() => setEventPage((value) => value + 1)}>
                                Next
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>Level</th>
                                    <th>Worker</th>
                                    <th>Category</th>
                                    <th>Video</th>
                                    <th>Message</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.Results.map((item) => (
                                    <tr key={item.Id}>
                                        <td>{formatDateTime(item.OccurredAtUtc)}</td>
                                        <td>{item.Level}</td>
                                        <td>{item.WorkerId ?? '-'}</td>
                                        <td>{item.Category ?? '-'}</td>
                                        <td>{item.VideoId ?? '-'}</td>
                                        <td>{item.Message}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {logs.Results.length === 0 ? <div className={styles.empty}>No logs found.</div> : null}
                        <div className={styles.actions}>
                            <button type="button" className={styles.secondary} disabled={logPage <= 1} onClick={() => setLogPage((value) => Math.max(1, value - 1))}>
                                Prev
                            </button>
                            <span className={styles.muted}>Page {logs.PageIndex} of {Math.max(1, logs.TotalPages)}</span>
                            <button type="button" className={styles.secondary} disabled={logPage >= logs.TotalPages} onClick={() => setLogPage((value) => value + 1)}>
                                Next
                            </button>
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};

export default AdminContentProcessingActivity;
